import { ChevronRight, CircleHelp, LifeBuoy, Moon, Pencil, ShieldCheck, Sun } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMe, useOrders } from "../api/hooks";
import { BrandHeader } from "../components/BrandHeader";
import { NeedTelegram } from "../components/NeedTelegram";
import { profile as text } from "../content/site";
import { GOAL_LABELS, SEX_LABELS, formatBmi } from "../lib/profile";
import { useSupport } from "../lib/support";
import { hasTelegramContext } from "../lib/telegram";
import { currentTheme, toggleTheme } from "../lib/theme";
import { OrderRow } from "./OrdersScreen";

const RECENT_ORDERS = 3;

/** Экран 13 ТЗ: имя и username, «Мои данные», «Мои заказы», «Дополнительно». */
export function ProfileScreen() {
  const navigate = useNavigate();
  const me = useMe();
  const orders = useOrders();
  const support = useSupport();
  const [theme, setTheme] = useState(currentTheme);

  if (!hasTelegramContext()) return <NeedTelegram what="Профиль" />;

  const name = [me.data?.firstName, me.data?.lastName].filter(Boolean).join(" ");
  const profile = me.data?.profile;
  const rows = [
    { label: text.sex, value: profile?.sex ? SEX_LABELS[profile.sex] : null },
    { label: text.height, value: profile?.heightCm != null ? `${profile.heightCm} см` : null },
    { label: text.weight, value: profile?.weightKg != null ? `${profile.weightKg} кг` : null },
    { label: text.bmi, value: profile?.bmi != null ? formatBmi(profile.bmi) : null },
    { label: text.goal, value: profile?.goal ? GOAL_LABELS[profile.goal] : null },
  ];

  return (
    <div className="screen">
      <div className="screen__header">
        <BrandHeader />
        {/* Иконка показывает, куда переключит, а не текущее состояние. */}
        <button
          type="button"
          className="theme-toggle"
          onClick={() => setTheme(toggleTheme())}
          aria-label={theme === "dark" ? "Светлая тема" : "Тёмная тема"}
        >
          {theme === "dark" ? <Sun size={18} strokeWidth={1.9} aria-hidden /> : <Moon size={18} strokeWidth={1.9} aria-hidden />}
        </button>
      </div>

      <h2 className="screen__title" style={{ marginTop: 8 }}>{name || "Профиль"}</h2>
      <p className="screen__subtitle">{me.data?.username ? `@${me.data.username}` : ""}</p>

      <div className="section">{text.dataTitle}</div>
      <div className="data-card">
        {rows.map((row) => (
          <div key={row.label} className="data-card__row">
            <span className="muted">{row.label}</span>
            <strong className={row.value ? "" : "muted"}>{row.value ?? text.notFilled}</strong>
          </div>
        ))}
        <button type="button" className="button button--ghost" style={{ marginTop: 10 }} onClick={() => navigate("/profile/edit")}>
          <Pencil size={16} strokeWidth={2} aria-hidden /> {text.edit}
        </button>
      </div>

      <div className="section">{text.ordersTitle}</div>
      <p className="small muted" style={{ marginBottom: 8 }}>{text.ordersSubtitle}</p>
      {orders.isPending && <div className="small muted">Загружаем…</div>}
      {orders.data?.items.length === 0 && <div className="small muted">{text.noOrders}</div>}
      {orders.data?.items.slice(0, RECENT_ORDERS).map((order) => (
        <OrderRow key={order.orderNumber} order={order} />
      ))}
      {(orders.data?.items.length ?? 0) > RECENT_ORDERS && (
        <button type="button" className="button button--ghost" onClick={() => navigate("/profile/orders")}>
          {text.allOrders} <ChevronRight size={16} strokeWidth={2} aria-hidden />
        </button>
      )}

      <div className="section">{text.moreTitle}</div>
      <div className="menu">
        <button type="button" className="menu__item" onClick={support.open} disabled={!support.available}>
          <LifeBuoy size={19} strokeWidth={1.8} aria-hidden />
          {text.support}
        </button>
        <button type="button" className="menu__item" onClick={() => navigate("/faq")}>
          <CircleHelp size={19} strokeWidth={1.8} aria-hidden />
          {text.faq}
        </button>
        {/* Пункт админки не показывается обычным пользователям, но это только
            удобство: доступ режет сервер на каждом /admin-эндпоинте. */}
        {me.data?.role === "admin" && (
          <button type="button" className="menu__item" onClick={() => navigate("/admin")}>
            <ShieldCheck size={19} strokeWidth={1.8} aria-hidden />
            {text.admin}
          </button>
        )}
      </div>
    </div>
  );
}
